/**
 * Wetter fuer den sichtbaren Zeitraum.
 *
 * Bewusst nachrangig: schlaegt der Abruf fehl - kein Netz, Dienst gestoert,
 * Bereich ausserhalb der Vorhersage - bleibt die Anzeige einfach leer. Ein
 * Kalender darf nie an einer Wetter-API haengen.
 *
 * Der Aufbau vermeidet `setState` im Effektkoerper: das Ergebnis wird ueber
 * einen Schluessel dem angefragten Zeitraum zugeordnet. Dadurch zeigt der
 * Kalender beim Blaettern nie kurz die Werte des vorigen Ausschnitts.
 */
import { useEffect, useMemo, useState } from 'react';

import {
  DEFAULT_LOCATION, type Coordinates, type DailyWeather,
} from '@/lib/weather/model';
import { addDays, differenceInDays, today } from '@/lib/datetime';
import type { LocalDate } from '@/domain/types';

/** Open-Meteo liefert bis zu 16 Tage voraus und nichts in der Vergangenheit. */
const MAX_DAYS_AHEAD = 15;

const EMPTY: Map<LocalDate, DailyWeather> = new Map();

interface Request {
  key: string;
  from: LocalDate;
  to: LocalDate;
}

/** Auf den Bereich zuschneiden, den der Dienst ueberhaupt abdeckt. */
function buildRequest(
  from: LocalDate | undefined,
  to: LocalDate | undefined,
  location: Coordinates,
  enabled: boolean,
): Request | null {
  if (!enabled || !from || !to) return null;

  const heute = today();
  if (to < heute) return null;

  const start = from < heute ? heute : from;
  if (differenceInDays(heute, start) > MAX_DAYS_AHEAD) return null;

  const end = differenceInDays(heute, to) > MAX_DAYS_AHEAD
    ? addDays(heute, MAX_DAYS_AHEAD)
    : to;

  return { key: `${start}|${end}|${location.latitude},${location.longitude}`, from: start, to: end };
}

export function useWeather(
  from: LocalDate | undefined,
  to: LocalDate | undefined,
  location: Coordinates = DEFAULT_LOCATION,
  enabled = true,
): Map<LocalDate, DailyWeather> {
  const request = useMemo(
    () => buildRequest(from, to, location, enabled),
    [from, to, location, enabled],
  );

  const [result, setResult] = useState<{ key: string; data: Map<LocalDate, DailyWeather> } | null>(null);

  useEffect(() => {
    if (!request) return;

    const controller = new AbortController();

    /*
     * Der Abruf wird NACHGELADEN, nicht statisch eingebunden.
     *
     * `openMeteo.ts` zieht zod mit (rund 26 kB gzip), und das Wetter ist
     * standardmaessig ausgeschaltet. Statisch eingebunden bezahlte jeder
     * Erststart fuer eine Funktion, die viele nie einschalten. Hier kostet es
     * nichts: die Stelle ist ohnehin asynchron.
     */
    import('@/lib/weather/openMeteo')
      .then(({ fetchDailyWeather }) =>
        fetchDailyWeather(request.from, request.to, location, controller.signal))
      .then((days) => setResult({ key: request.key, data: new Map(days.map((d) => [d.date, d])) }))
      .catch(() => { /* Wetter ist Beiwerk - Fehler bleiben still. */ });

    return () => controller.abort();
  }, [request, location]);

  // Nur anzeigen, was zum aktuell sichtbaren Zeitraum gehoert.
  return request && result?.key === request.key ? result.data : EMPTY;
}
